import React, { useEffect, useMemo } from "react";
import styles from "./ui.module.scss";
import { useEventStore } from "../../store";
import moment from "moment";
import redirectTo from "../../../../utils/redirectTo";
import { useRouter } from "next/router";
import { get_city_label } from "../../../../utils/misc";

const EventScheduleTitle = () => {
  return <h4 className={styles.EventScheduleTitle}>Расписание</h4>;
};

const EventScheduleDateInfoDay = ({ date, hard = false }) => {
  const { selectedDate, selectedTimeSlot } = useEventStore();

  const isSelectedDate = useMemo(() => {
    if (selectedTimeSlot) return true;
    return !!selectedDate;
  }, [selectedTimeSlot, selectedDate]);

  return (
    <div>
      <img
        src="/static/images/eventIcons/GrayCalendarBlank.svg"
        alt="Календарь"
      />
      {!hard && (
        <span>
          {isSelectedDate ? date.format("D MMMM YYYY") : "дд.мм.гггг"}
        </span>
      )}
      {hard && <span>{date.format("D MMMM YYYY")}</span>}
    </div>
  );
};

const EventScheduleDateInfoTime = ({ date, hard = false }) => {
  const { selectedTime, selectedTimeSlot } = useEventStore();

  const isSelectedTime = useMemo(() => {
    if (selectedTimeSlot) return true;
    return !!selectedTime;
  }, [selectedTimeSlot, selectedTime]);

  return (
    <div>
      <img src="/static/images/eventIcons/GrayWatch.svg" alt="Часы" />
      {!hard && <span>{isSelectedTime ? date.format("HH:mm") : "00:00"}</span>}
      {hard && <span>{date.format("HH:mm")}</span>}
    </div>
  );
};

const EventScheduleDateInfo = ({ date, hard = false }) => {
  return (
    <div className={styles.EventScheduleDateInfo}>
      <EventScheduleDateInfoDay date={date} hard={hard} />
      <EventScheduleDateInfoTime date={date} hard={hard} />
    </div>
  );
};

const EventScheduleFullness = ({ timeslot, orEmpty = false }) => {
  const { getEventTimeSlotByJoinedTimeSlot, eventTimeSlots } = useEventStore();

  const currTimeSlot = useMemo(() => {
    if (timeslot) return getEventTimeSlotByJoinedTimeSlot(timeslot);
    else return null;
  }, [eventTimeSlots, timeslot]);

  if (currTimeSlot)
    return (
      <div className={styles.EventScheduleFullness}>
        Заполненность: {currTimeSlot.joined_count || 0}/
        {currTimeSlot.count || 0}
      </div>
    );

  if (orEmpty)
    return (
      <div className={styles.EventScheduleFullness}>
        Заполненность: {currTimeSlot?.joined_count || 0}/
        {currTimeSlot?.count || 0}
      </div>
    );

  return null;
};

const EventScheduleUserStatus = () => {
  const { eventUserStatus } = useEventStore();
  const { action } = eventUserStatus;
  return (
    <div className={styles.EventScheduleUserStatus} data-action={action}>
      {action === "donation_added" ? "Донация добавлена" : "Вы записаны"}
    </div>
  );
};

const EventScheduleButton = ({
  disabled,
  isBig = false,
  after = false,
  one = false,
}) => {
  const {
    eventData,
    eventSignUp,
    eventChangeTimeSlot,
    addDonationStore,
    selectedTimeSlot,
    bloodStationsStore,
    userNotSignedUpBut,
    authStore,
  } = useEventStore();
  const router = useRouter();
  const { user_status } = eventData;

  const addDonation = async () => {
    if (!authStore?.user && userNotSignedUpBut) {
      redirectTo(`/login/?next=${encodeURIComponent(router?.asPath)}`);
      return;
    }
    let data = {
      ProfileAddDonationPlaceTypesCheck:
        eventData.category.id === 2 ? "onTheRoad" : "stationary",
      selectedStationaryPoint: eventData.category.id === 1,
      selectedCity: {
        value: eventData.city_id,
        label: get_city_label(eventData.city),
      },
      donation: {
        blood_class: "blood",
        donate_at: after
          ? moment(eventData?.start_date).format("YYYY-MM-DD")
          : selectedTimeSlot?.date,
        payment_type: "free",
        blood_station_id: eventData?.blood_station?.id || eventData?.blood_station,
        city_id: eventData.city_id,
        is_out: eventData.category.id === 2,
        payment_cost: 1,
        plan_date: after
          ? moment(eventData?.start_date).format("YYYY-MM-DD")
          : selectedTimeSlot?.date,
        volume: 1,
        with_image: true,
      },
    };
    if (eventData?.blood_station) {
      const res = await bloodStationsStore.loadOne(eventData.blood_station.id);
      data["selectedStation"] = {
        label: res.title,
        value: res.id,
      };
    }
    addDonationStore.setPreData(data);
    redirectTo("/profile/add_donation");
  };

  if (after && userNotSignedUpBut) {
    return (
      <div className={styles.EventScheduleButton} data-type={"add_donation"}>
        <button disabled={disabled} onClick={addDonation}>
          {authStore?.user ? "Добавить донацию" : "Войти"}
        </button>
      </div>
    );
  }

  if (!user_status?.action)
    return (
      <div
        className={styles.EventScheduleButton}
        data-type={"join"}
        data-test={true}
        data-one={one}
      >
        <button
          onClick={() =>
            redirectTo(`/login/?next=${encodeURIComponent(router?.asPath)}`)
          }
        >
          Присоединиться
        </button>
      </div>
    );

  switch (user_status.action) {
    case "add_donation":
      return (
        <div className={styles.EventScheduleButton} data-type={"add_donation"}>
          <button disabled={disabled} onClick={addDonation}>
            Добавить донацию
          </button>
        </div>
      );
    case "change":
      return (
        <div className={styles.EventScheduleButton} data-type={"change"}>
          <button disabled={disabled} onClick={eventChangeTimeSlot}>
            Изменить запись
          </button>
        </div>
      );
    case "create":
      return (
        <div
          className={styles.EventScheduleButton}
          data-type={`create${isBig ? "_big" : ""}`}
        >
          <button disabled={disabled} onClick={eventSignUp}>
            Записаться
          </button>
        </div>
      );
    case "donation_added":
      return null;
    default:
      return null;
  }
};

const EventScheduleCard = () => {
  const {
    eventTimeSlots,
    availableDates,
    eventUserStatus,
    userNotSignedUpBut,
  } = useEventStore();

  if (userNotSignedUpBut) {
    return (
      <>
        <EventScheduleTitle />
        <div className={styles.EventScheduleCard}>
          <EventScheduleUserNotSignedUpButCanAddDonation />
        </div>
      </>
    );
  }

  if (!eventTimeSlots || (!availableDates.length && !eventUserStatus.joined))
    return null;

  return (
    <>
      <EventScheduleTitle />
      <div className={styles.EventScheduleCard}>
        <EventScheduleAlreadySignedUp />
        <EventScheduleNotSignedUp />
      </div>
    </>
  );
};

const EventScheduleUserNotSignedUpButCanAddDonation = () => {
  return (
    <div className={styles.EventScheduleUserNotSignedUpButCanAddDonation}>
      <EventScheduleUserNotSignedUpButStatus />
      <EventScheduleButton disabled={false} after />
    </div>
  );
};

const EventScheduleUserNotSignedUpButStatus = () => {
  const { authStore } = useEventStore();
  if (authStore?.user)
    return (
      <div className={styles.EventScheduleUserStatus} data-type={"but"}>
        Если ходили, добавьте донацию
      </div>
    );
  else
    return (
      <div className={styles.EventScheduleUserStatus} data-type={"but"}>
        Войдите, чтобы добавить донацию
      </div>
    );
};

const EventScheduleNotSignedUp = () => {
  const { eventUserStatus } = useEventStore();

  const { joined } = eventUserStatus;
  if (joined) return null;

  return (
    <div>
      <EventScheduleNotSignedUpOneDate />
      <EventScheduleNotSignedUpMoreDate />
    </div>
  );
};

const EventScheduleNotSignedUpMoreDate = () => {
  const { availableDates, availableTimes } = useEventStore();

  if (availableDates?.length <= 1 && availableTimes?.length <= 1) return null;

  return (
    <div className={styles.EventScheduleNotSignedUpMoreDate}>
      <EventScheduleNotSignedUpMoreDatePickerDate />
      <EventScheduleNotSignedUpMoreDatePickerTime />
      <EventScheduleNotSignedUpMoreDateBottomBlock />
    </div>
  );
};

const EventScheduleNotSignedUpMoreDatePickerDate = () => {
  const { availableDates } = useEventStore();
  if (availableDates.length <= 1) return null;

  return (
    <div className={styles.EventSchedulePicker}>
      <div className={styles.EventSchedulePicker_Title}>Выберите дату</div>
      <div className={styles.EventSchedulePicker_Content}>
        {availableDates.map((item) => (
          <EventScheduleNotSignedUpMoreDatePickerDateItem
            item={item}
            key={`date_${item.id}`}
          />
        ))}
      </div>
    </div>
  );
};

const EventScheduleNotSignedUpMoreDatePickerTime = () => {
  const { availableTimes, selectedDate, selectedTimeSlot, allAvailableTimes } =
    useEventStore();

  const isShow = useMemo(() => {
    if (selectedDate) return true;
    return !!selectedTimeSlot;
  }, [selectedDate, selectedTimeSlot]);

  if (!isShow) return null;

  if (allAvailableTimes.length <= 1 || !availableTimes) return null;

  return (
    <div className={styles.EventSchedulePicker}>
      <div className={styles.EventSchedulePicker_Title}>Выберите время</div>
      <div className={styles.EventSchedulePicker_Content}>
        {availableTimes
          .filter(
            (item) =>
              moment(item.date).add(item.start_time).toDate() >= new Date()
          )
          ?.map((item) => (
            <EventScheduleNotSignedUpMoreDatePickerTimeItem
              item={item}
              key={`time_${item.id}`}
            />
          ))}
      </div>
    </div>
  );
};

const EventScheduleNotSignedUpMoreDateBottomBlock = () => {
  const { selectedTimeSlot, selectedDate, selectedTime } = useEventStore();

  const timeslotDate = useMemo(() => {
    if (selectedTimeSlot)
      return moment(selectedTimeSlot.date).add(selectedTimeSlot.start_time);
    return moment(selectedDate).add(selectedTime);
  }, [selectedTimeSlot, selectedTime, selectedDate]);

  const isButtonDisabled = useMemo(() => {
    if (!selectedTimeSlot) return true;
    return selectedTimeSlot.joined_count >= selectedTimeSlot.count;
  }, [selectedTimeSlot]);

  return (
    <div className={styles.EventScheduleNotSignedUpMoreDateBottomBlock}>
      <div
        className={styles.EventScheduleNotSignedUpMoreDateBottomBlock_Container}
      >
        <EventScheduleDateInfo date={timeslotDate} />
        <EventScheduleFullness timeslot={selectedTimeSlot} orEmpty />
      </div>
      <EventScheduleButton disabled={isButtonDisabled} isBig />
    </div>
  );
};

const EventScheduleNotSignedUpOneDate = () => {
  const {
    availableDates,
    selectedTimeSlot,
    eventData,
    availableTimes,
    setSelectedDate,
    allAvailableTimes,
    setSelectedTimeSlot,
    selectedDate,
  } = useEventStore();

  useEffect(() => {
    if (availableDates.length === 1 && selectedDate !== availableDates[0].date)
      setSelectedDate(availableDates[0].date);
    if (
      allAvailableTimes.length === 1 &&
      selectedTimeSlot !== allAvailableTimes[0]
    )
      setSelectedTimeSlot(allAvailableTimes[0]);
  }, [availableDates, allAvailableTimes]);

  const date = useMemo(() => {
    if (selectedTimeSlot)
      return moment(selectedTimeSlot?.date).add(selectedTimeSlot?.start_time);
    else return null;
  }, [selectedTimeSlot]);

  const isButtonDisabled = useMemo(() => {
    return selectedTimeSlot?.joined_count >= selectedTimeSlot?.count;
  }, [eventData]);

  if (availableDates?.length > 1 || availableTimes?.length > 1) return null;

  if (!selectedTimeSlot || !date) return null;

  return (
    <div className={styles.EventScheduleNotSignedUpOneDate}>
      <EventScheduleDateInfo date={date} />
      <EventScheduleFullness timeslot={selectedTimeSlot} />
      <EventScheduleButton disabled={isButtonDisabled} one={true} />
    </div>
  );
};

const EventScheduleAlreadySignedUp = () => {
  const { eventUserStatus } = useEventStore();

  const { joined } = eventUserStatus;
  if (!joined) return null;

  return (
    <div className={styles.EventScheduleAlreadySignedUp}>
      <EventScheduleAlreadySignedUpBefore />
      <EventScheduleAlreadySignedUpAfter />
      <EventScheduleAlreadySignedUpDonationAdded />
      <EventScheduleAlreadySignedUpEnded />
    </div>
  );
};

const EventScheduleAlreadySignedUpEnded = () => {
  const { eventUserStatus } = useEventStore();
  const { action, joined } = eventUserStatus;

  const { timeslot } = joined;

  const timeslotDate = useMemo(() => {
    if (!timeslot) return null;
    return moment(timeslot.date).add(timeslot.start_time);
  }, [eventUserStatus]);

  if (action) return null;
  return (
    <div className={styles.EventScheduleAlreadySignedUpEnded}>
      <EventScheduleDateInfo date={timeslotDate} hard />
      <EventScheduleUserStatus />
    </div>
  );
};

const EventScheduleAlreadySignedUpBefore = () => {
  const {
    eventUserStatus,
    setSelectedTimeSlot,
    getEventTimeSlotByJoinedTimeSlot,
  } = useEventStore();
  const { action, joined } = eventUserStatus;

  if (action !== "change") return null;

  const { timeslot } = joined;

  useEffect(() => {
    if (eventUserStatus?.joined?.timeslot)
      setSelectedTimeSlot(
        getEventTimeSlotByJoinedTimeSlot(eventUserStatus.joined.timeslot)
      );
  }, [eventUserStatus]);

  const timeslotDate = useMemo(() => {
    if (!timeslot) return null;
    return moment(timeslot.date).add(timeslot.start_time);
  }, [eventUserStatus]);

  if (!timeslot) return null;

  return (
    <div className={styles.EventScheduleAlreadySignedUpBefore}>
      <div className={styles.EventScheduleAlreadySignedUpBefore_Container}>
        <EventScheduleDateInfo date={timeslotDate} />
        <EventScheduleFullness timeslot={timeslot} />
        <EventScheduleUserStatus />
      </div>
      <EventScheduleButton disabled={false} />
    </div>
  );
};

const EventScheduleAlreadySignedUpAfter = () => {
  const {
    eventUserStatus,
    setSelectedTimeSlot,
    getEventTimeSlotByJoinedTimeSlot,
  } = useEventStore();
  const { action, joined } = eventUserStatus;

  if (action !== "add_donation") return null;

  const { timeslot } = joined;

  useEffect(() => {
    if (eventUserStatus?.joined?.timeslot)
      setSelectedTimeSlot(
        getEventTimeSlotByJoinedTimeSlot(eventUserStatus.joined.timeslot)
      );
  }, [eventUserStatus]);

  const timeslotDate = useMemo(() => {
    if (!timeslot) return null;
    return moment(timeslot.date).add(timeslot.start_time);
  }, [eventUserStatus]);

  if (!timeslot) return null;

  return (
    <div className={styles.EventScheduleAlreadySignedUpAfter}>
      <EventScheduleDateInfo date={timeslotDate} />
      <EventScheduleUserStatus />
      <EventScheduleButton disabled={false} />
    </div>
  );
};

const EventScheduleAlreadySignedUpDonationAdded = () => {
  const {
    eventUserStatus,
    setSelectedTimeSlot,
    getEventTimeSlotByJoinedTimeSlot,
  } = useEventStore();
  const { action, joined } = eventUserStatus;

  if (action !== "donation_added") return null;

  const { timeslot } = joined;

  useEffect(() => {
    if (eventUserStatus?.joined?.timeslot)
      setSelectedTimeSlot(
        getEventTimeSlotByJoinedTimeSlot(eventUserStatus.joined.timeslot)
      );
  }, [eventUserStatus]);

  const timeslotDate = useMemo(() => {
    if (!timeslot) return null;
    return moment(timeslot.date).add(timeslot.start_time);
  }, [eventUserStatus]);

  if (!timeslot) return null;

  return (
    <div className={styles.EventScheduleAlreadySignedUpAfter}>
      <EventScheduleDateInfo date={timeslotDate} />
      <EventScheduleUserStatus />
      <EventScheduleButton disabled={false} />
    </div>
  );
};

const EventScheduleNotSignedUpMoreDatePickerTimeItem = ({ item }) => {
  const { setSelectedTimeSlot, setSelectedTime, selectedTime } =
    useEventStore();

  const changeTime = () => {
    setSelectedTime(item.start_time);
    setSelectedTimeSlot(item);
  };

  return (
    <div
      className={styles.EventScheduleItem}
      onClick={changeTime}
      data-active={selectedTime === item.start_time ? "true" : "false"}
    >
      {item.start_time.slice(0, -3)}
    </div>
  );
};

const EventScheduleNotSignedUpMoreDatePickerDateItem = ({ item }) => {
  const {
    setSelectedTimeSlot,
    setSelectedTime,
    selectedDate,
    setSelectedDate,
    allAvailableTimes,
  } = useEventStore();

  const changeDate = () => {
    setSelectedDate(item.date);
    if (allAvailableTimes?.length === 1) {
      setSelectedTimeSlot(item);
      setSelectedTime(item.start_time);
    } else {
      setSelectedTime(null);
      setSelectedTimeSlot(null);
    }
  };

  return (
    <div
      className={styles.EventScheduleItem}
      data-active={selectedDate === item.date ? "true" : "false"}
      onClick={changeDate}
    >
      {moment(item.date)?.format("DD.MM.YYYY")}
    </div>
  );
};

export default EventScheduleCard;
